import React, { useMemo, useRef, useCallback } from 'react';
import { AutocompleteWithErrorStatus } from '../../AutocompleteWithErrorStatus';
import { AddressOption } from 'src/types/address';

export interface AccepterProps {
  data: AddressOption[];
  disabled?: boolean;
  errorMessage?: string;
  errorText?: string;
  hasError?: boolean;
  hideErrorIcon?: boolean;
  loading?: boolean;
  name?: string;
  onAddressSelected?: (value?: AddressOption) => void;
  onChange?: (value: AddressOption | string) => void;
  value?: AddressOption | string;
}

const AddressLookupAccepter = ({
  value,
  errorMessage,
  errorText,
  hasError,
  onAddressSelected,
  onChange,
  ...rest
}: AccepterProps) => {
  const pendingSelectionRef = useRef<AddressOption | undefined>(undefined);

  const innerValue = useMemo(() => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.value;
  }, [value]);

  const handleSelect = useCallback(
    (option?: AddressOption) => {
      pendingSelectionRef.current = option;
      onAddressSelected?.(option);
    },
    [onAddressSelected]
  );

  const handleChange = useCallback(
    (stringValue: string) => {
      if (pendingSelectionRef.current?.value === stringValue) {
        const pending = pendingSelectionRef.current;
        pendingSelectionRef.current = undefined;
        onChange?.(
          pending.components ? pending : (pending.label ?? stringValue)
        );
      } else {
        onChange?.(stringValue);
      }
    },
    [onChange]
  );

  return (
    <AutocompleteWithErrorStatus
      {...rest}
      errorMessage={errorText ?? errorMessage ?? ''}
      onChange={handleChange}
      onSelect={handleSelect}
      showError={hasError}
      value={innerValue}
    />
  );
};

export default AddressLookupAccepter;
